import { BORDER_RADIUS, BOX_SHADOW } from "@/constants";
import { CSSObject, alpha, styled } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { MUIStyledCommonProps } from "@mui/system";

import { ICardContainer } from "@/components/Card/card.interface";
import * as React from "react";

// Parameterised with the *material* theme, not `@mui/system`'s bare one. Unparameterised, the
// `sx` it inherits is typed against a themeless `{}`, which rejects every palette path a caller
// would want to pass.
interface StyledDiv extends MUIStyledCommonProps<Theme> {
  secondaryColor?: boolean;
  shadow?: boolean;
  customHeight?: string;
}

// Supplying shouldForwardProp replaces MUI's default filter rather than adding to
// it, so the props the engine normally consumes have to be excluded here too —
// otherwise `sx` lands on the DOM node as sx="[object Object]".
const StyledDiv = styled("div", {
  shouldForwardProp: (prop) =>
    !["secondaryColor", "shadow", "customHeight", "sx", "as", "theme"].includes(
      prop as string
    ),
})<StyledDiv>(
  ({ theme, secondaryColor, shadow, customHeight }): CSSObject => ({
    width: "100%",
    height: customHeight ?? "100%",
    backgroundColor: secondaryColor
      ? theme.palette.background.default
      : theme.palette.background.paper,
    display: "flex",
    borderRadius: BORDER_RADIUS,
    flexDirection: "column",
    boxShadow: shadow ? BOX_SHADOW : "none",
    /*
      Derived from the palette, and heavier than it was.

      `#E0E6ED` was hardcoded — a blue-grey that does not move with any of the
      four white-label palettes, and it measured 1.19:1 against the card it was
      drawing. Since the surface itself is only ~1.05:1 from the page, that
      hairline was the ONLY thing defining a card, which is most of why these
      screens read as "just some boxes".

      0.16 of `secondary.main` lands near 1.4:1, inside the band every shipping
      design system uses for a surface boundary. Carbon moved its own subtle
      border up for exactly this reason — it had been at 1.32:1 and was judged
      too light.
    */
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.16)}`,
  })
);

export default function CardContainer({
  children,
  secondaryColor = false,
  sx,
  shadow = true,
  customHeight,
}: CardContainerProps): ReturnType<React.FC> {
  return (
    <StyledDiv
      secondaryColor={secondaryColor}
      sx={sx}
      shadow={shadow}
      customHeight={customHeight}
    >
      {children}
    </StyledDiv>
  );
}

type CardContainerProps = ICardContainer;
